import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Check, ArrowLeft, Minus, Plus, Phone, MessageCircle } from "lucide-react";
import api, { formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products/${slug}`)
      .then((res) => setProduct(res.data))
      .catch(() => navigate("/produse"))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-sky-200 border-t-sky-600" />
      </div>
    );
  }
  if (!product) return null;

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 lg:px-12" data-testid="product-detail-page">
      <Link to="/produse" className="mb-8 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-sky-600">
        <ArrowLeft className="h-4 w-4" /> Înapoi la produse
      </Link>

      <div className="grid gap-12 lg:grid-cols-2">
        <div className="flex items-center justify-center rounded-3xl bg-sky-50/60 p-10">
          <img src={product.image} alt={product.name} className="h-[460px] w-auto object-contain drop-shadow-xl" />
        </div>

        <div className="flex flex-col justify-center">
          <span className="label-caps text-sky-600">{product.category}</span>
          <h1 className="mt-3 font-display text-3xl font-black tracking-tight text-slate-900 lg:text-4xl" data-testid="product-title">
            {product.name}
          </h1>
          <div className="mt-4 flex items-center gap-3">
            <span className="font-display text-3xl font-extrabold text-slate-900" data-testid="product-price">{formatPrice(product.price)}</span>
            {product.old_price && <span className="text-lg text-slate-400 line-through">{formatPrice(product.old_price)}</span>}
          </div>
          <p className="mt-5 leading-relaxed text-slate-600">{product.long_description || product.description}</p>

          {product.features?.length > 0 && (
            <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
              {product.features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-slate-700">
                  <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-100 text-emerald-600">
                    <Check className="h-3 w-3" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <div className="flex items-center rounded-full border border-slate-200">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="grid h-11 w-11 place-items-center text-slate-500 hover:text-sky-600" data-testid="qty-decrease">
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-10 text-center font-bold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="grid h-11 w-11 place-items-center text-slate-500 hover:text-sky-600" data-testid="qty-increase">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <Button size="lg" className="flex-1 rounded-full bg-sky-600 text-base hover:bg-sky-500" onClick={() => addItem(product, qty)} data-testid="product-add-to-cart">
              <ShoppingCart className="mr-2 h-5 w-5" /> Adaugă în coș
            </Button>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <a
              href={`https://wa.me/40734154845?text=${encodeURIComponent(`Bună ziua! Aș dori să comand produsul: ${product.name}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="product-whatsapp"
              className="flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-5 py-3 text-base font-semibold text-white transition-colors hover:bg-emerald-600"
            >
              <MessageCircle className="h-5 w-5" /> Comandă pe WhatsApp
            </a>
            <a
              href="tel:+40734154845"
              data-testid="product-call"
              className="flex items-center justify-center gap-2 rounded-full border border-slate-200 px-5 py-3 text-base font-semibold text-slate-700 transition-colors hover:border-sky-300 hover:text-sky-600"
            >
              <Phone className="h-5 w-5" /> Sună-ne: +40 734 154 845
            </a>
          </div>

          <div className="mt-6 rounded-2xl bg-emerald-50 p-4 text-sm text-emerald-800">
            🎁 <strong>Promoție:</strong> pachet 2+1 GRATIS — 2 detergenți + 1 balsam cadou.{" "}
            <Link to="/oferte" className="font-semibold underline">Vezi pachetele</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
